import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import { useGetJobInvoiceStatsQuery } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import {
  Funnel,
  FunnelChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useMemo } from "react";

type InvoiceStatusKey =
  | "jobCompleteNotBilled"
  | "billedNotPaidCurrent"
  | "billedNotPaidOverdue"
  | "paid";

type StatsByStatus = Record<
  InvoiceStatusKey,
  { allTime: number; last30Days: number }
>;

const statusOrder: InvoiceStatusKey[] = [
  "jobCompleteNotBilled",
  "billedNotPaidCurrent",
  "billedNotPaidOverdue",
  "paid",
];

const statusLabel = (key: InvoiceStatusKey) => {
  switch (key) {
    case "jobCompleteNotBilled":
      return "Job Complete Not Billed";
    case "billedNotPaidCurrent":
      return "Billed Not Paid (Current)";
    case "billedNotPaidOverdue":
      return "Billed Not Paid (Overdue)";
    case "paid":
      return "Paid";
    default:
      return key;
  }
};

function buildStatsMap(
  rows:
    | Array<{
        invoiceStatus: string;
        yearToDateAmount?: any;
        last30DaysAmount?: any;
      }>
    | undefined
): StatsByStatus {
  const base: StatsByStatus = {
    jobCompleteNotBilled: { allTime: 0, last30Days: 0 },
    billedNotPaidCurrent: { allTime: 0, last30Days: 0 },
    billedNotPaidOverdue: { allTime: 0, last30Days: 0 },
    paid: { allTime: 0, last30Days: 0 },
  };

  (rows ?? []).forEach((r) => {
    const key = r.invoiceStatus as InvoiceStatusKey;
    if (!(key in base)) return;

    base[key] = {
      allTime: Number(r.yearToDateAmount ?? 0),
      last30Days: Number(r.last30DaysAmount ?? 0),
    };
  });

  return base;
}

function FunnelCard({
  title,
  data,
}: {
  title: string;
  data: Array<{ name: string; value: number }>;
}) {
  return (
    <Card>
      <MDBox p={3}>
        <MDBox
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDTypography variant="h5">{title}</MDTypography>
        </MDBox>

        <MDBox sx={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <FunnelChart>
              <Tooltip
                formatter={(value: any, name: any) => [
                  formatCentsToUSD(Number(value) || 0),
                  name,
                ]}
              />
              <Funnel dataKey="value" data={data} isAnimationActive={false}>
                <LabelList
                  dataKey="name"
                  position="right"
                  formatter={(label: any) => String(label)}
                />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default function InvoiceMetricCards() {
  const { data, loading } = useGetJobInvoiceStatsQuery();

  const stats = useMemo(
    () => buildStatsMap(data?.getJobInvoiceStats),
    [data?.getJobInvoiceStats]
  );

  const allTimeFunnelData = useMemo(
    () =>
      statusOrder.map((k) => ({
        name: statusLabel(k),
        value: stats[k].allTime,
      })),
    [stats]
  );

  const last30DaysFunnelData = useMemo(
    () =>
      statusOrder.map((k) => ({
        name: statusLabel(k),
        value: stats[k].last30Days,
      })),
    [stats]
  );

  return (
    <>
      <MDBox mb={2}>
        <MDTypography variant="h5">
          {loading ? "Loading…" : "All time"}
        </MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        {statusOrder.map((key) => (
          <Grid key={`allTime-${key}`} item xs={12} md={6} lg={3}>
            <DefaultStatisticsCard
              title={statusLabel(key)}
              count={formatCentsToUSD(stats[key].allTime || 0)}
              countVariant="h4"
            />
          </Grid>
        ))}
      </Grid>

      <MDBox mt={3} mb={2}>
        <MDTypography variant="h5">
          Last 30 Days
        </MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        {statusOrder.map((key) => (
          <Grid key={`last30-${key}`} item xs={12} md={6} lg={3}>
            <DefaultStatisticsCard
              title={statusLabel(key)}
              count={formatCentsToUSD(stats[key].last30Days || 0)}
              countVariant="h4"
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
}
